import { useCallback, useMemo, useRef, useEffect } from 'react';
import { MapLocationData } from '@hero/web-components';

export interface UseMapChangeTrackingOptions {
  /** Current locations */
  locations: MapLocationData[];
  /** Initial locations */
  initialLocations: MapLocationData[];
  /** Whether to enable change tracking */
  enableConfirmLocationsButton: boolean;
}

/**
 * Hook for tracking location changes and unsaved state
 * Compares current locations with confirmed/saved state
 */
export function useMapChangeTracking({
  locations,
  initialLocations,
  enableConfirmLocationsButton,
}: UseMapChangeTrackingOptions) {
  // Track last confirmed locations to detect changes
  const lastConfirmedLocationsRef = useRef<MapLocationData[]>([]);

  // Helper function to compare locations and detect changes
  const hasLocationChanges = useCallback(
    (current: MapLocationData[], lastConfirmed: MapLocationData[]): boolean => {
      // Different lengths means changes
      if (current.length !== lastConfirmed.length) {
        return true;
      }

      // Compare each location's coordinates and key properties
      for (let i = 0; i < current.length; i++) {
        const curr = current[i];
        const last = lastConfirmed[i];

        if (!last) return true;

        // Compare coordinates (with small tolerance for floating point)
        const latDiff = Math.abs((curr.cords?.lat || 0) - (last.cords?.lat || 0));
        const lngDiff = Math.abs((curr.cords?.lng || 0) - (last.cords?.lng || 0));
        if (latDiff > 0.0001 || lngDiff > 0.0001) {
          return true;
        }

        // Compare other key properties
        if (curr.isAccurateLocation !== last.isAccurateLocation) {
          return true;
        }

        // Compare saved location IDs
        const currSavedId = curr.savedLocation?._id?.toString() || curr.savedLocationId?.toString();
        const lastSavedId = last.savedLocation?._id?.toString() || last.savedLocationId?.toString();
        if (currSavedId !== lastSavedId) {
          return true;
        }

        // For saved locations: detect metadata changes (name, contacts, client, etc.)
        if (curr.savedLocation || last.savedLocation) {
          if (curr.savedLocation?.name !== last.savedLocation?.name) return true;
          if (curr.savedLocation?.contacts?.length !== last.savedLocation?.contacts?.length) return true;
          // relations-v2: the owning counterparty is `client` (legacy `customer` dropped).
          if (curr.savedLocation?.client?._id?.toString() !== last.savedLocation?.client?._id?.toString()) return true;
          // Also compare updatedAt when available (covers any other field changes)
          const currUpdatedAt = curr.savedLocation?.doc?.updatedAt?.toString();
          const lastUpdatedAt = last.savedLocation?.doc?.updatedAt?.toString();
          if (currUpdatedAt && lastUpdatedAt && currUpdatedAt !== lastUpdatedAt) return true;
        }
      }

      return false;
    },
    [],
  );

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!enableConfirmLocationsButton) return false;
    const compareTo = lastConfirmedLocationsRef.current.length === 0 ? initialLocations : lastConfirmedLocationsRef.current;
    const result = hasLocationChanges(locations, compareTo);
    return result;
  }, [locations, initialLocations, enableConfirmLocationsButton, hasLocationChanges]);

  // Check if a specific location has been edited (compared to last confirmed)
  const isLocationEdited = useCallback(
    (index: number): boolean => {
      if (!enableConfirmLocationsButton) return false;
      if (index < 0 || index >= locations.length) return false;

      const currentLocation = locations[index];
      const lastConfirmed = lastConfirmedLocationsRef.current;

      if (lastConfirmed.length === 0) {
        // Compare against initial locations
        if (index >= initialLocations.length) return true; // New location
        return hasLocationChanges([currentLocation], [initialLocations[index]]);
      }

      if (index >= lastConfirmed.length) return true; // New location

      return hasLocationChanges([currentLocation], [lastConfirmed[index]]);
    },
    [locations, initialLocations, enableConfirmLocationsButton, hasLocationChanges],
  );

  // Initialize last confirmed locations when component mounts or initialLocations change
  useEffect(() => {
    if (initialLocations.length > 0) {
      const hasChanged =
        lastConfirmedLocationsRef.current.length === 0 ||
        hasLocationChanges(initialLocations, lastConfirmedLocationsRef.current);

      if (hasChanged) {
        lastConfirmedLocationsRef.current = initialLocations.map(loc => ({ ...loc }));
      }
    } else if (initialLocations.length === 0) {
      lastConfirmedLocationsRef.current = [];
    }
  }, [initialLocations, hasLocationChanges]);

  // Update confirmed locations (called when changes are saved)
  const updateConfirmedLocations = useCallback((newLocations: MapLocationData[]) => {
    lastConfirmedLocationsRef.current = newLocations.map(loc => ({ ...loc }));
  }, []);

  return {
    hasUnsavedChanges,
    isLocationEdited,
    lastConfirmedLocationsRef,
    updateConfirmedLocations,
  };
}
